import classNames from "classnames"
import React from "react"

import Button from "components/button"
import Icon from "icons"
import { useClientContext } from "layouts/application-group"
import request from "request"
import styles from "./result-box.module.scss"
import { toast } from "react-toastify"

const ResultBox = () => {
  const { group, OCRFileList, setGroup, pendingOCR, setPendingOCR } =
    useClientContext()

  const [pendingScoring, setPendingScoring] = React.useState(false)

  const OCRButtonIsBlocked = React.useMemo(
    () => !!OCRFileList.find((file) => !file.file),
    [OCRFileList],
  )

  const ScoringButtonIsBlocked = React.useMemo(() => {
    const verify = !group.applications.every(
      (application) =>
        application.customerId &&
        application.applicationId &&
        application.scoring_log &&
        application.scoring_log.age !== null &&
        application.scoring_log.familyStatus !== null &&
        application.scoring_log.age !== null &&
        application.scoring_log.dependents !== null &&
        application.scoring_log.monthlyIncome !== null &&
        application.scoring_log.workExperience !== null &&
        application.scoring_log.productType !== null &&
        application.scoring_log.gender !== null &&
        application.scoring_log.education !== null &&
        application.scoring_log.employmentLevel !== null &&
        application.scoring_log.employmentSphere !== null &&
        application.scoring_log.coBorrower !== null &&
        application.scoring_log.collateral !== null &&
        application.scoring_log.loanDuration !== null &&
        application.scoring_log.cbj_black_list !== null &&
        application.scoring_log.internal_black_list !== null &&
        application.scoring_log.total_personal_expenses_monthly !== null &&
        application.scoring_log.active_ahli_bank_monthly_payment !== null &&
        // application.scoring_log.selected_monthly_payments.length > 0 &&
        application.scoring_log.inspector_legal_cases !== null,
    )

    return verify
  }, [group.applications])

  const handleCalculateOCR = async () => {
    setPendingOCR(true)
    try {
      const fd = new FormData()
      OCRFileList.forEach((OCRFile) => {
        fd.append("files", OCRFile.file!)
        fd.append("application_ids", String(OCRFile.id))
      })
      await request({
        url: `/applications/group/${group.id}/ocr`,
        method: "POST",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      })
      const res = await request.get(`/applications/group/${group.id}`)
      setGroup(res.data)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred."
      toast.error(`Error calculating OCR: ${errorMessage}`)
    } finally {
      setPendingOCR(false)
    }
  }

  const handleCalculateScoring = async () => {
    if (!group.ocr_approved) return toast.error("CRIF file not uploaded")
    setPendingScoring(true)
    request
      .post(`/applications/group/${group.id}/scoring`, {
        applications: group.applications.map((application) => ({
          application_id: application.id,
          data: {
            productType: application.scoring_log?.productType,
            branch: application.scoring_log?.branch,
            gender: application.scoring_log?.gender,
            familyStatus: application.scoring_log?.familyStatus,
            education: application.scoring_log?.education,
            employmentLevel: application.scoring_log?.employmentLevel,
            employmentSphere: application.scoring_log?.employmentSphere,
            coBorrower: application.scoring_log?.coBorrower,
            collateral: application.scoring_log?.collateral,
            loanDuration: application.scoring_log?.loanDuration,
            age: application.scoring_log?.age,
            monthlyIncome: application.scoring_log?.monthlyIncome,
            dependents: application.scoring_log?.dependents,
            workExperience: application.scoring_log?.workExperience,
            cbj_black_list: application.scoring_log?.cbj_black_list,
            internal_black_list: application.scoring_log?.internal_black_list,
            inspector_legal_cases:
              application.scoring_log?.inspector_legal_cases,
            loan_type: application.scoring_log?.loan_type,
            property_value: application.scoring_log?.property_value,
            property_age_at_end: application.scoring_log?.property_age_at_end,
            property_outside_regulations:
              application.scoring_log?.property_outside_regulations,
            car_evaluation_score: application.scoring_log?.car_evaluation_score,
            credit_card_limit: application.scoring_log?.credit_card_limit,
            target_market_list: application.scoring_log?.target_market_list,
            length_of_service: application.scoring_log?.length_of_service,
            total_personal_expenses_monthly:
              application.scoring_log?.total_personal_expenses_monthly,
            active_ahli_bank_monthly_payment:
              application.scoring_log?.active_ahli_bank_monthly_payment,
            selected_monthly_payments:
              application.scoring_log?.selected_monthly_payments,
          },
        })),
      })
      .then(() => {
        request
          .get(`/applications/group/${group.id}`)
          .then((res) => res.data)
          .then((res) => setGroup(res))
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error calculating scoring: ${errorMessage}`)
      })
      .finally(() => {
        setPendingScoring(false)
      })
  }

  return (
    <div className={styles.result_list}>
      <div className={styles.result_box}>
        <div className={styles.title}>Group status (ID {group.id})</div>
        {group.is_approved_group && (
          <div className={styles.finalResult}>
            <Icon
              size={120}
              name={
                group.is_approved_group === "approved"
                  ? "tickCircle"
                  : "closeFilled"
              }
              className={classNames(
                group.is_approved_group === "approved"
                  ? styles.green
                  : group.is_approved_group === "rejected"
                    ? styles.red
                    : styles.yellow,
              )}
            />
            <div
              className={classNames(
                styles.finalResultText,
                group.is_approved_group === "approved"
                  ? styles.green
                  : group.is_approved_group === "rejected"
                    ? styles.red
                    : styles.yellow,
              )}
            >
              {group.is_approved_group.charAt(0).toUpperCase() +
                group.is_approved_group.slice(1)}
            </div>
          </div>
        )}
        <div className={styles.item}>
          <div className={styles.key}>OCR</div>
          {group.ocr_approved ? (
            <div
              className={classNames(
                styles.value,
                !group.ocr_approved ? styles.red : styles.green,
              )}
            >
              {group.ocr_approved ? "File provided" : "File not provided"}
            </div>
          ) : (
            <Button
              blocked={OCRButtonIsBlocked}
              color={OCRButtonIsBlocked ? "grey" : "blue"}
              pending={pendingOCR}
              onClick={handleCalculateOCR}
            >
              Calculate
            </Button>
          )}
        </div>

        {
          <div className={styles.item}>
            <div className={styles.key}>Risk</div>
            {group.risk_approved ? (
              <div
                className={classNames(styles.value, {
                  [styles.red]: group.risk_approved === "rejected",
                  [styles.yellow]: group.risk_approved === "review",
                  [styles.green]:
                    group.risk_approved !== "rejected" &&
                    group.risk_approved !== "review",
                })}
              >
                {`${
                  group.risk_approved.charAt(0).toUpperCase() +
                  group.risk_approved.slice(1)
                } (${group.risk_count_summary ?? 0})`}
              </div>
            ) : (
              <span>No risk assessment</span>
            )}
          </div>
        }

        <div className={styles.item}>
          <div className={styles.key}>Scoring</div>
          {group.scoring_approved ? (
            <div
              className={classNames(
                styles.value,
                group.scoring_approved === "rejected"
                  ? styles.red
                  : group.scoring_approved === "review"
                    ? styles.yellow
                    : styles.green,
              )}
            >
              {group.scoring_approved.charAt(0).toUpperCase() +
                group.scoring_approved.slice(1)}{" "}
              ({group.prediction?.toFixed(2)}%)
            </div>
          ) : (
            <Button
              blocked={ScoringButtonIsBlocked}
              color={ScoringButtonIsBlocked ? "grey" : "blue"}
              pending={pendingScoring}
              onClick={handleCalculateScoring}
            >
              Calculate
            </Button>
          )}
        </div>
        <div className={styles.item}>
          <div className={styles.key}>DBR</div>
          {group?.dbr_is_approved ? (
            <div
              className={classNames(
                styles.value,
                group?.dbr_is_approved === "rejected"
                  ? styles.red
                  : group?.dbr_is_approved === "review"
                    ? styles.yellow
                    : styles.green,
              )}
            >
              {group?.dbr_is_approved.charAt(0).toUpperCase() +
                group?.dbr_is_approved.slice(1)}{" "}
            </div>
          ) : (
            "No decision"
          )}
        </div>
        <div className={styles.item}>
          <div className={styles.key}>Stop factors</div>
          {group?.stop_factors_is_approved ? (
            <div
              className={classNames(
                styles.value,
                group?.stop_factors_is_approved === "rejected"
                  ? styles.red
                  : group?.stop_factors_is_approved === "review"
                    ? styles.yellow
                    : styles.green,
              )}
            >
              {group?.stop_factors_is_approved.charAt(0).toUpperCase() +
                group?.stop_factors_is_approved.slice(1)}{" "}
            </div>
          ) : (
            "No decision"
          )}
        </div>
      </div>
    </div>
  )
}

export default ResultBox
